import { useState } from "react";
import {
  ComposedChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Line,
  PieChart, Pie, Cell,
} from "recharts";
import { C, S } from '../constants';
import { fmt, fmtUAH } from '../utils';
import KpiCard from '../components/KpiCard';
import InfoTip from '../components/InfoTip';
import Img from '../components/Img';
import ProdLink from '../components/ProdLink';
import DatePicker from '../components/DatePicker';

function goTo(path) {
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export default function OverviewPage({ kpi, prevKpi, daily, cats, trend, dead, realDead, dateFrom, dateTo, isPreset, onApplyDates, CHART_COLORS, setCatModal, data, loss = [], negativeReviews = [], profitKpi, abc = [], ffBypass = [], outOfStockProds = [], blockedProds = [], notInPromoProds = [], goToProductsWithFilter, revivedStock = [], pinnedData = [], reviewDaily = [], marketplace = 'rozetka' }) {
  const [chartMode, setChartMode] = useState("orders");

  const calcTrend = (curr, prev) => {
    if (prev == null) return null;
    if (prev === 0) return null;
    return (curr - prev) / prev * 100;
  };
  const trendAvgCheck = prevKpi ? calcTrend(kpi.avg_check, prevKpi.avg_check) : null;
  const trendToday    = calcTrend(kpi.orders_today, kpi.orders_yesterday);

  const todayStr = new Date().toISOString().slice(0, 10);
  const todayRow = daily.find(d => d.date === todayStr) || {};
  const fakeToday = todayRow.fake || 0;

  const yesterdayStr = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
  const yesterdayRow = daily.find(d => d.date === yesterdayStr) || {};
  const dayBeforeYesterdayStr = new Date(Date.now() - 2 * 86400000).toISOString().slice(0, 10);
  const dayBeforeYesterdayRow = daily.find(d => d.date === dayBeforeYesterdayStr) || {};
  const trendYesterday = calcTrend(kpi.orders_yesterday, dayBeforeYesterdayRow.orders_count ?? null);

  const last7 = daily.slice(-7);
  const ordersWeek     = last7.reduce((s, d) => s + (d.successful || 0) + (d.cancelled || 0) + (d.pending || 0) + (d.fake || 0), 0);
  const fakeWeek       = last7.reduce((s, d) => s + (d.fake || 0), 0);
  const revenueWeekFromDaily = last7.reduce((s, d) => s + (d.revenue || 0), 0);
  const successfulWeek = last7.reduce((s, d) => s + (d.successful || 0), 0);
  const cancelledWeek  = last7.reduce((s, d) => s + (d.cancelled  || 0), 0);
  const pendingWeek    = last7.reduce((s, d) => s + (d.pending    || 0), 0);
  const trendOrdersWeek = calcTrend(kpi.orders_week ?? null, kpi.orders_prev_week ?? null);

  const thisMonthStr = new Date().toISOString().slice(0, 7);
  const monthDays = daily.filter(d => d.date.startsWith(thisMonthStr));
  const successfulMonth = monthDays.reduce((s, d) => s + (d.successful || 0), 0);
  const cancelledMonth  = monthDays.reduce((s, d) => s + (d.cancelled  || 0), 0);
  const pendingMonth    = monthDays.reduce((s, d) => s + (d.pending    || 0), 0);

  const ordersMonth  = kpi.orders_month ?? 0;
  const fakeMonth    = kpi.fake_month ?? 0;
  const revenueMonth = kpi.revenue_month ?? 0;
  const trendMonth   = calcTrend(kpi.orders_month ?? null, kpi.orders_prev_month ?? null);

  const topProducts = abc.slice(0, 10);
  const maxRevenue = topProducts[0]?.revenue || 1;

  const pieData = [
    { name: "Успішні",   value: kpi.successful_orders, color: C.green  },
    { name: "В обробці", value: kpi.pending_orders,    color: C.yellow },
    { name: "Відмінені", value: kpi.cancelled_orders,  color: C.pink   },
  ];

  return (
    <>
      <div style={{ marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: C.text1, letterSpacing: "-0.4px", lineHeight: 1.2 }}>Аналітика продавця</div>
        </div>
      </div>

      {/* ── МОНІТОРИНГ: завжди live, не залежить від date picker ── */}
      <div className="kpi-grid">
        <KpiCard
          label="Сьогодні"
          value={fmt(kpi.orders_today ?? 0)}
          fakeCnt={fakeToday}
          sub={kpi.revenue_today > 0 ? fmtUAH(kpi.revenue_today) : "замовлень"}
          color={C.green}
          tip="Замовлення за сьогодні (з 00:00), усі статуси."
          trend={trendToday}
          statusRow={[
            { icon: "✓", value: todayRow.successful || 0, color: C.green  },
            { icon: "⏳", value: todayRow.pending    || 0, color: C.yellow },
            { icon: "✕", value: todayRow.cancelled  || 0, color: C.pink   },
          ]}
        />
        <KpiCard
          label="Вчора"
          value={fmt(kpi.orders_yesterday ?? 0)}
          sub={kpi.revenue_yesterday > 0 ? fmtUAH(kpi.revenue_yesterday) : "замовлень"}
          color={C.blue}
          tip="Замовлення за вчора (весь день), усі статуси."
          trend={trendYesterday}
          statusRow={[
            { icon: "✓", value: yesterdayRow.successful || 0, color: C.green  },
            { icon: "⏳", value: yesterdayRow.pending    || 0, color: C.yellow },
            { icon: "✕", value: yesterdayRow.cancelled  || 0, color: C.pink   },
          ]}
        />
        <KpiCard
          label="7 днів"
          value={fmt(ordersWeek)}
          fakeCnt={fakeWeek}
          sub={fmtUAH(revenueWeekFromDaily)}
          color={C.blue}
          tip="Останні 7 днів — усі статуси. В дужках — самозакупи."
          trend={trendOrdersWeek}
          statusRow={[
            { icon: "✓", value: successfulWeek, color: C.green  },
            { icon: "⏳", value: pendingWeek,   color: C.yellow },
            { icon: "✕", value: cancelledWeek, color: C.pink   },
          ]}
        />
        <KpiCard
          label="Місяць"
          value={fmt(ordersMonth)}
          fakeCnt={fakeMonth}
          sub={fmtUAH(revenueMonth)}
          color={C.purple}
          tip="З 1-го числа поточного місяця — усі статуси. В дужках — самозакупи."
          trend={trendMonth}
          statusRow={[
            { icon: "✓", value: successfulMonth, color: C.green  },
            { icon: "⏳", value: pendingMonth,   color: C.yellow },
            { icon: "✕", value: cancelledMonth, color: C.pink   },
          ]}
        />
        <KpiCard
          label="Середній чек"
          value={fmtUAH(kpi.avg_check)}
          sub={`${fmt(kpi.successful_orders)} успішних`}
          color={C.yellow}
          tip="Середня сума успішного замовлення за обраний аналітичний період."
          trend={trendAvgCheck}
        />
        {profitKpi && (
          <KpiCard
            label="Прибуток"
            value={fmtUAH(profitKpi.total)}
            sub={profitKpi.margin != null
              ? `${profitKpi.margin.toFixed(1)}% маржа · ${profitKpi.covered} SKU`
              : `${profitKpi.covered} SKU з собівартістю`}
            color={profitKpi.total >= 0 ? C.green : C.pink}
            tip="Чистий прибуток по товарах де введена собівартість. Формула: Ціна − Комісія − 3% платіжки − Доставка − Собівартість."
          />
        )}
      </div>

      {/* ── АНАЛІЗ: date picker + графіки ── */}
      <div style={{
        borderTop: `1px solid ${C.border}`,
        paddingTop: 18,
        marginBottom: 14,
      }}>
        {/* Section label + date picker */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 14, flexWrap: "wrap" }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: C.text4, textTransform: "uppercase", letterSpacing: "0.8px", whiteSpace: "nowrap" }}>
            Аналіз за:
          </span>
          <DatePicker
            from={dateFrom || data?.period?.from || ""}
            to={dateTo || data?.period?.to || ""}
            onApply={onApplyDates}
          />
        </div>

        {/* Compact summary bar when custom period selected */}
        {!isPreset && (
          <div style={{
            padding: "8px 14px", marginBottom: 14, borderRadius: 10,
            background: `${C.purple}10`, border: `1px solid ${C.purple}25`,
            fontSize: 12,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
              <span style={{ color: C.text3, fontWeight: 600 }}>
                {data?.period?.from} — {data?.period?.to}
              </span>
              <span style={{ color: C.text4 }}>·</span>
              <span style={{ color: C.text1 }}><span style={{ color: C.green, fontWeight: 700 }}>{fmt(kpi.successful_orders)}</span> успішних</span>
              <span style={{ color: C.text4 }}>·</span>
              <span style={{ color: C.text1 }}><span style={{ fontWeight: 700 }}>{fmtUAH(kpi.total_revenue)}</span> виручки</span>
              <span style={{ color: C.text4 }}>·</span>
              <span style={{ color: C.text1 }}><span style={{ fontWeight: 700 }}>{fmtUAH(kpi.avg_check)}</span> середній чек</span>
              {kpi.fake_orders > 0 && <>
                <span style={{ color: C.text4 }}>·</span>
                <span style={{ color: C.yellow }}>{fmt(kpi.fake_orders)} викупів</span>
              </>}
            </div>
            {prevKpi && (() => {
              const revTrend = calcTrend(kpi.total_revenue, prevKpi.total_revenue);
              const ordTrend = calcTrend(kpi.successful_orders, prevKpi.successful_orders);
              const trendSpan = (val, fmt2) => {
                if (val == null) return null;
                const pos = val >= 0;
                return <span style={{ color: pos ? C.green : C.pink, fontWeight: 700 }}>{pos ? "+" : ""}{fmt2(val)}</span>;
              };
              return (
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginTop: 6, paddingTop: 6, borderTop: `1px solid ${C.border}`, color: C.text3 }}>
                  <span>vs попередній період:</span>
                  <span>виручка {trendSpan(revTrend, v => `${v.toFixed(1)}%`)}</span>
                  <span>·</span>
                  <span>замовлення {trendSpan(ordTrend, v => `${v.toFixed(1)}%`)}</span>
                  <span>·</span>
                  <span>попередній: {fmtUAH(prevKpi.total_revenue)} · {fmt(prevKpi.successful_orders)} успішних</span>
                </div>
              );
            })()}
          </div>
        )}

        {/* 3 mini-charts: Donut | FF/FBS lines | Buybacks bars */}
        <div className="mini-charts-grid">

          {/* Donut: orders by status */}
          <div style={{ ...S.card, display: "flex", flexDirection: "column", gap: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.text1 }}>Статуси замовлень</div>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <PieChart width={140} height={130}>
                <Pie
                  data={pieData}
                  cx={70} cy={65} innerRadius={36} outerRadius={56}
                  dataKey="value" paddingAngle={2}
                >
                  {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip
                  content={({ active, payload }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0]?.payload;
                    return (
                      <div style={{ background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 12 }}>
                        <div style={{ color: d.color, fontWeight: 700 }}>{d.name}</div>
                        <div style={{ color: C.text1 }}>{fmt(d.value)} шт.</div>
                      </div>
                    );
                  }}
                />
              </PieChart>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {pieData.map(({ name, value, color }) => (
                <div key={name} style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
                  <span style={{ color }}>● {name}</span>
                  <span style={{ color: C.text1, fontWeight: 700 }}>{fmt(value)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* FF vs FBS revenue lines */}
          <div style={S.card}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.text1, marginBottom: 8, display: "flex", alignItems: "center", gap: 12 }}>
              FF / FBS виручка
              <span style={{ fontSize: 10, color: C.purple, fontWeight: 600 }}>— FF</span>
              <span style={{ fontSize: 10, color: C.green, fontWeight: 600 }}>— FBS</span>
            </div>
            <ResponsiveContainer width="100%" height={140}>
              <ComposedChart data={daily} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis dataKey="date" tick={{ fill: C.text4, fontSize: 9 }} tickFormatter={d => d.slice(5)} />
                <YAxis tick={{ fill: C.text4, fontSize: 9 }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}к` : v} width={32} />
                <Tooltip content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0]?.payload || {};
                  return (
                    <div style={{ background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 11 }}>
                      <div style={{ color: C.text2, marginBottom: 4, fontWeight: 600 }}>{label}</div>
                      <div style={{ color: C.purple }}>FF: {fmtUAH(d.revenue_ff || 0)}</div>
                      <div style={{ color: C.green }}>FBS: {fmtUAH(d.revenue_fbs || 0)}</div>
                    </div>
                  );
                }} />
                <Line type="monotone" dataKey="revenue_ff" stroke={C.purple} strokeWidth={2} dot={false} name="FF" />
                <Line type="monotone" dataKey="revenue_fbs" stroke={C.green} strokeWidth={2} dot={false} name="FBS" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {/* Buybacks bar chart */}
          <div style={S.card}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.text1, marginBottom: 8 }}>Викупи по днях</div>
            <ResponsiveContainer width="100%" height={140}>
              <ComposedChart data={daily} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                <XAxis dataKey="date" tick={{ fill: C.text4, fontSize: 9 }} tickFormatter={d => d.slice(5)} />
                <YAxis tick={{ fill: C.text4, fontSize: 9 }} width={24} allowDecimals={false} />
                <Tooltip content={({ active, payload, label }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0]?.payload || {};
                  return (
                    <div style={{ background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 11 }}>
                      <div style={{ color: C.text2, marginBottom: 4, fontWeight: 600 }}>{label}</div>
                      <div style={{ color: C.yellow }}>Викупи: {d.fake || 0} шт.</div>
                    </div>
                  );
                }} />
                <Bar dataKey="fake" fill={`${C.yellow}99`} radius={[3, 3, 0, 0]} name="Викупи" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Main charts: Sales dynamics + Categories */}
        <div className="main-charts-grid">
          <div style={S.card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: C.text1, letterSpacing: "-0.2px" }}>Динаміка продажів</span>
                <InfoTip text={"Стовпці — кількість або виручка за день.\nРежим «Замовлення»: розбивка по статусам + лінія загальної кількості.\nРежим «Виручка»: виручка за добу."} />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {[["orders", "Замовлення"], ["revenue", "Виручка"]].map(([m, l]) => (
                  <div key={m} onClick={() => setChartMode(m)} style={S.btn(chartMode === m, C.blue)}>{l}</div>
                ))}
                {chartMode === "orders" && (
                  <div style={{ display: "flex", gap: 12, fontSize: 11 }}>
                    <span style={{ color: C.blue }}>— Всього</span>
                    <span style={{ color: C.green }}>■ Успішні</span>
                    <span style={{ color: C.pink }}>■ Відмін.</span>
                    {daily.some(d => d.fake > 0) && <span style={{ color: C.yellow }}>■ Викупи</span>}
                  </div>
                )}
              </div>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              {chartMode === "revenue" ? (
                <ComposedChart data={daily} barSize={14}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                  <XAxis dataKey="date" tick={{ fill: C.text4, fontSize: 10 }} />
                  <YAxis tick={{ fill: C.text4, fontSize: 10 }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}к` : v} />
                  <Tooltip content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0]?.payload || {};
                    const isToday = label === todayStr;
                    return (
                      <div style={{ background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", fontSize: 12 }}>
                        <div style={{ color: C.text2, marginBottom: 6, fontWeight: 600 }}>{label}{isToday ? " (сьогодні, неповний день)" : ""}</div>
                        <div style={{ color: C.green, fontWeight: 700 }}>{fmtUAH(d.revenue || 0)}</div>
                        {d.successful > 0 && <div style={{ color: C.text3, fontSize: 11, marginTop: 2 }}>{d.successful} успішних замовлень</div>}
                      </div>
                    );
                  }} />
                  <Bar dataKey="revenue" radius={[4,4,0,0]} fill={`${C.green}99`} label={false} />
                </ComposedChart>
              ) : (
                <ComposedChart data={daily.map(d => ({ ...d, total: (d.orders_count || 0) + (d.fake || 0) }))} barSize={10}>
                  <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                  <XAxis dataKey="date" tick={{ fill: C.text4, fontSize: 10 }} />
                  <YAxis tick={{ fill: C.text4, fontSize: 10 }} />
                  <Tooltip content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    const d = payload[0]?.payload || {};
                    return (
                      <div style={{ background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", fontSize: 12 }}>
                        <div style={{ color: C.text2, marginBottom: 6, fontWeight: 600 }}>{label}</div>
                        {d.successful > 0 && <div style={{ color: C.green }}>Успішні: {d.successful} шт.</div>}
                        {d.cancelled > 0 && <div style={{ color: C.pink }}>Відмін.: {d.cancelled} шт.</div>}
                        {d.pending > 0 && <div style={{ color: C.yellow }}>В обробці: {d.pending} шт.</div>}
                        {d.fake > 0 && <div style={{ color: `${C.yellow}aa` }}>Викупи: {d.fake} шт.</div>}
                        <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 5, paddingTop: 5, color: C.blue, fontWeight: 700 }}>
                          Всього: {d.total} шт.
                        </div>
                        {d.revenue > 0 && <div style={{ color: C.text3, fontSize: 11, marginTop: 2 }}>{fmtUAH(d.revenue)} виручки</div>}
                      </div>
                    );
                  }} />
                  <Bar dataKey="successful" fill={`${C.green}99`} stackId="s" name="Успішні" radius={[3,3,0,0]} />
                  <Bar dataKey="cancelled" fill={`${C.pink}88`} stackId="s" name="Відмін." />
                  <Bar dataKey="pending" fill={`${C.yellow}66`} stackId="s" name="В обробці" />
                  {daily.some(d => (d.fake || 0) > 0) && (
                    <Bar dataKey="fake" fill={`${C.yellow}44`} name="Викупи" radius={[2,2,0,0]} />
                  )}
                  <Line type="monotone" dataKey="total" stroke={C.blue} strokeWidth={2.5} dot={false} name="Всього" />
                </ComposedChart>
              )}
            </ResponsiveContainer>
          </div>

          {/* Reviews trend */}
          {(() => {
            const chartData = [...reviewDaily].sort((a, b) => a.date > b.date ? 1 : -1).slice(-90);
            const hasReviews = chartData.length > 0;
            return (
              <div style={S.card}>
                <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                  Відгуки
                  <InfoTip text={"Нові відгуки за день.\nПозитивні: 4-5★ · Негативні: 1-3★"} />
                </div>
                {!hasReviews ? (
                  <div style={{ fontSize: 11, color: C.text4, padding: "20px 0", textAlign: "center" }}>
                    Дані з'являться після наступної синхронізації відгуків (щодня о 05:00)
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={140}>
                    <ComposedChart data={chartData} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                      <XAxis dataKey="date" tick={{ fill: C.text4, fontSize: 9 }} tickFormatter={d => d.slice(5)} />
                      <YAxis tick={{ fill: C.text4, fontSize: 10 }} />
                      <Tooltip content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        const d = payload[0]?.payload || {};
                        return (
                          <div style={{ background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 12 }}>
                            <div style={{ color: C.text2, marginBottom: 4, fontWeight: 600 }}>{label}</div>
                            {d.positive > 0 && <div style={{ color: C.green }}>★ Позитивні: {d.positive}</div>}
                            {d.negative > 0 && <div style={{ color: C.pink }}>★ Негативні: {d.negative}</div>}
                          </div>
                        );
                      }} />
                      <Line type="monotone" dataKey="positive" stroke={C.green} strokeWidth={2.5} dot={false} name="Позитивні 4-5★" />
                      <Line type="monotone" dataKey="negative" stroke={C.pink} strokeWidth={2.5} dot={false} name="Негативні 1-3★" />
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
                <div style={{ display: "flex", gap: 12, marginTop: 6, fontSize: 10, color: C.text4 }}>
                  <span><span style={{ color: C.green }}>—</span> Позитивні (4-5★)</span>
                  <span><span style={{ color: C.pink }}>—</span> Негативні (1-3★)</span>
                </div>
              </div>
            );
          })()}

          {/* Categories */}
          <div style={S.card}>
            <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
              Дохід по категоріях
              <InfoTip text={"Розподіл виручки по товарних категоріях.\nКлікніть на категорію — побачите топ товарів у ній.\n\nПолоса = частка від максимальної категорії."} />
            </div>
            <div style={{ overflowY: "auto", maxHeight: 230 }}>
              {cats.slice(0, 12).map((cat, i) => {
                const maxR = cats[0]?.revenue || 1;
                const totalR = cats.reduce((s, c) => s + (c.revenue || 0), 0) || 1;
                const pct = Math.round(cat.revenue / totalR * 100);
                return (
                  <div key={i} style={{ marginBottom: 9, cursor: "pointer" }}
                    onClick={() => setCatModal(cat.name)}
                    title="Клік → показати товари категорії">
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
                      <span style={{ color: CHART_COLORS[i % CHART_COLORS.length], maxWidth: 140,
                        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        textDecoration: "underline dotted" }}>
                        {cat.name || "Без категорії"}
                      </span>
                      <span style={{ color: C.text1, fontWeight: 700 }}>{fmtUAH(cat.revenue)} <span style={{ color: C.text4, fontWeight: 400 }}>{pct}%</span></span>
                    </div>
                    <div style={{ height: 4, background: C.bg3, borderRadius: 2, overflow: "hidden" }}>
                      <div style={{ height: "100%", background: CHART_COLORS[i % CHART_COLORS.length],
                        width: Math.max(4, cat.revenue / maxR * 100) + "%", borderRadius: 2 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ── ВІТРИНА: обрані товари ── */}
      {pinnedData.length > 0 && (
        <div style={{ ...S.card, marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <span style={{ fontSize: 18 }}>★</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 700 }}>Вітрина</div>
              <div style={{ fontSize: 11, color: C.text4, marginTop: 1 }}>обрані товари · {pinnedData.length} шт.</div>
            </div>
          </div>
          <div style={{ display: "grid", gap: 10, gridTemplateColumns: `repeat(${Math.min(pinnedData.length, Math.max(2, Math.floor(window.innerWidth / 200)))}, 1fr)` }}>
            {pinnedData.map((p, i) => {
              const stock = p.is_ff ? p.ff_stock : p.fbs_stock;
              const stockOk = stock >= 3;
              const maxBar = Math.max(...p.daily_sales, 1);
              const ratingColor = p.top_rating === null ? C.pink : p.top_rating <= 3 ? C.yellow : C.green;
              return (
                <div key={i} style={{ ...S.card, background: C.bg2, border: `1px solid ${C.border}`, borderRadius: 12, padding: "12px 12px 10px", display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
                  {/* Header */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <Img src={p.image_url} size={28} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <ProdLink name={p.name} article={p.article} productId={p.product_id} marketplace={p.marketplace} style={{ fontSize: 11, color: C.text2, fontWeight: 600 }} />
                    </div>
                  </div>
                  {/* Metrics row */}
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color: C.blue, lineHeight: 1 }}>{p.week_qty}</div>
                      <div style={{ fontSize: 9, color: C.text4, marginTop: 2 }}>прод. 7д</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 18, fontWeight: 800, color: stockOk ? C.green : C.pink, lineHeight: 1 }}>{stock}</div>
                      <div style={{ fontSize: 9, color: C.text4, marginTop: 2 }}>{p.is_ff ? "FF" : "FBS"}</div>
                    </div>
                    {p.position != null && (
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 18, fontWeight: 800, color: p.position <= 10 ? C.green : p.position <= 30 ? C.yellow : C.text3, lineHeight: 1 }}>#{p.position}</div>
                        <div style={{ fontSize: 9, color: C.text4, marginTop: 2 }}>позиція</div>
                      </div>
                    )}
                    {p.agg_rating != null && (
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 18, fontWeight: 800, color: ratingColor, lineHeight: 1 }}>{p.agg_rating}</div>
                        <div style={{ fontSize: 9, color: C.text4, marginTop: 2 }}>★ {p.review_count || 0}</div>
                      </div>
                    )}
                  </div>
                  {/* 7-day sparkline */}
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
                    {p.daily_sales.map((v, di) => (
                      <div key={di} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                        <div style={{
                          width: "100%", background: v > 0 ? C.blue : `${C.blue}20`,
                          height: v > 0 ? Math.max(3, Math.round(v / maxBar * 20)) : 2,
                          borderRadius: "2px 2px 0 0",
                        }} />
                        <div style={{ fontSize: 8, color: v > 0 ? C.text3 : C.text4, lineHeight: 1 }}>{v || "·"}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Problem cards — 7 categories */}
      {(() => {
        const cards = [
          { icon: "📦", label: "Нема запасу",       count: outOfStockProds.length, color: C.pink,     sub: "терміново докупити",      tip: "Активні FF-товари з нульовим залишком на складі Rozetka", filterId: "out_of_stock_ff" },
          { icon: "🔒", label: "Заблокований",       count: blockedProds.length,    color: C.pink,     sub: "прихований модератором",  tip: "Товари приховані модератором Rozetka (upload_status=11)", filterId: "blocked" },
          { icon: "📉", label: "Товар в мінус",      count: loss.length,            color: C.pink,     sub: "продається у збиток",     tip: "Ціна − комісія − доставка − собівартість < 0",            filterId: "loss" },
          { icon: "💤", label: "Мертвий запас",      count: realDead.length,        color: C.yellow,   sub: "немає продажів >7 днів",  tip: "Товари з ненульовим залишком але без продажів понад 7 днів", filterId: "dead" },
          { icon: "⚡", label: "FF байпас",          count: ffBypass.length,        color: "#fb923c",  sub: "відвантаження з FBS",     tip: "FF-товари з замовленнями що йдуть через FBS",             filterId: "ff_bypass" },
          { icon: "★",  label: "Негативні відгуки",  count: negativeReviews.length, color: "#f87171",  sub: "рейтинг ≤ 3",             tip: "Товари з незакритими негативними відгуками (рейтинг ≤ 3)", filterId: "neg_reviews" },
          { icon: "📧", label: "Не в промо",         count: notInPromoProds.length, color: C.blue,     sub: "немає промо ціни",        tip: "Rozetka вимагає промо ціну, але вона не виставлена",      filterId: "not_promo" },
        ];

        const totalProblems = cards.reduce((s, c) => s + c.count, 0);

        return (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.text4, textTransform: "uppercase", letterSpacing: "0.8px", marginBottom: 12 }}>
              Проблеми
              {totalProblems > 0 && <span style={{ color: C.pink, marginLeft: 6 }}>· {totalProblems}</span>}
            </div>
            <div className="problem-cards-grid">
              {cards.map(card => {
                const hasProb = card.count > 0;
                return (
                  <div
                    key={card.label}
                    title={card.tip}
                    onClick={hasProb && goToProductsWithFilter ? () => goToProductsWithFilter(card.filterId) : undefined}
                    style={{
                      background: hasProb ? `${card.color}12` : `${C.green}08`,
                      border: `1px solid ${hasProb ? card.color + "40" : C.green + "20"}`,
                      borderTop: `3px solid ${hasProb ? card.color : C.green + "50"}`,
                      borderRadius: 14, padding: "18px 18px 14px",
                      cursor: hasProb ? "pointer" : "default",
                      transition: "all .15s",
                      display: "flex", flexDirection: "column",
                    }}
                    onMouseEnter={e => { if (hasProb) { e.currentTarget.style.background = `${card.color}1e`; e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = `0 4px 16px ${card.color}20`; } }}
                    onMouseLeave={e => { e.currentTarget.style.background = hasProb ? `${card.color}12` : `${C.green}08`; e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}
                  >
                    <div style={{ fontSize: 22, marginBottom: 10, lineHeight: 1 }}>{card.icon}</div>
                    <div style={{ fontSize: 10, color: hasProb ? C.text2 : C.text4, textTransform: "uppercase", letterSpacing: "0.8px", fontWeight: 700, marginBottom: 8 }}>
                      {card.label}
                    </div>
                    <div style={{ fontSize: 32, fontWeight: 800, color: hasProb ? card.color : C.green, fontVariantNumeric: "tabular-nums", lineHeight: 1, marginBottom: 6 }}>
                      {hasProb ? card.count : "✓"}
                    </div>
                    <div style={{ fontSize: 11, color: hasProb ? card.color + "bb" : C.green, fontWeight: 500, flex: 1 }}>
                      {hasProb ? card.sub : "Все добре"}
                    </div>
                    {hasProb && (
                      <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${card.color}25`, fontSize: 10, color: card.color, fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span>Переглянути товари</span>
                        <span>→</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* ── ПРОГНОЗ: товари що скоро закінчаться ── */}
      {(() => {
        const oos = abc
          .filter(p => p.days_to_zero != null && p.days_to_zero <= 14 && (p.ff_stock > 0 || p.fbs_stock > 0))
          .sort((a, b) => (a.days_to_zero ?? 999) - (b.days_to_zero ?? 999))
          .slice(0, 10);
        if (oos.length === 0) return null;
        return (
          <div style={{ ...S.card, marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <span style={{ fontSize: 18 }}>⏳</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>Прогноз закінчення стоку</div>
                <div style={{ fontSize: 11, color: C.text4 }}>товари що закінчуються за поточним темпом продажів</div>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {oos.map(p => {
                const isRed = p.days_to_zero <= 7;
                const oosDate = new Date(Date.now() + p.days_to_zero * 86400000)
                  .toLocaleDateString("uk-UA", { day: "numeric", month: "short" });
                const stock = p.is_ff ? p.ff_stock : p.fbs_stock;
                return (
                  <div key={p.rozetka_id} style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "6px 10px", borderRadius: 8,
                    background: isRed ? `${C.red}0d` : `${C.yellow}0d`,
                    border: `1px solid ${isRed ? C.red : C.yellow}30`,
                  }}>
                    <Img src={p.image_url} size={28} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <ProdLink name={p.name} article={p.article} productId={p.rozetka_id}
                        style={{ fontSize: 12, color: C.text1, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }} />
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: isRed ? C.red : C.yellow }}>
                        ~{oosDate}
                      </div>
                      <div style={{ fontSize: 11, color: C.text3 }}>
                        {p.days_to_zero} дн · {stock} шт · {p.velocity}/дн
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Зростаючі продажі — тільки реальне зростання (без "ожило") */}
      {(() => {
        const trendToShow = trend.filter(p => !p.was_revived);
        return (
          <div style={{ ...S.card, marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 20 }}>📈</span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>Зростаючі продажі</div>
                  <div style={{ fontSize: 11, color: C.text4, marginTop: 1 }}>
                    порівняння: поточні 14 днів vs попередні 14 днів
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                {trendToShow.length > 0 && <span style={{ fontSize: 11, color: C.green, fontWeight: 600 }}>↑ {trendToShow.length} ростуть</span>}
                <span style={{ fontSize: 12, color: C.blue, cursor: "pointer", textDecoration: "underline dotted" }} onClick={() => goTo("/trending")}>Всі →</span>
              </div>
            </div>
            {trendToShow.length === 0
              ? <div style={{ color: C.text4, fontSize: 13, padding: "12px 0" }}>Немає зростаючих товарів за останні 14 днів</div>
              : (
                <div className="trending-grid">
                  {trendToShow.slice(0, 8).map((p, i) => {
                    const color = C.green;
                    const maxVal = Math.max(p.curr_week_qty, p.prev_week_qty, 1);
                    const prevH = Math.max(4, Math.round((p.prev_week_qty / maxVal) * 32));
                    const currH = Math.max(4, Math.round((p.curr_week_qty / maxVal) * 32));
                    return (
                      <div key={i} style={{
                        background: `${color}0e`, border: `1px solid ${color}30`,
                        borderTop: `3px solid ${color}`, borderRadius: 12,
                        padding: "14px 14px 12px", display: "flex", flexDirection: "column", gap: 6,
                        transition: "all .15s",
                      }}
                        onMouseEnter={e => { e.currentTarget.style.background = `${color}18`; e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = `0 4px 14px ${color}20`; }}
                        onMouseLeave={e => { e.currentTarget.style.background = `${color}0e`; e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = ""; }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <Img src={p.image_url} size={24} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <ProdLink name={p.name} article={p.article} productId={p.product_id} marketplace={p.marketplace} style={{ fontSize: 10, color: C.text3, fontWeight: 400 }} />
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
                          <div>
                            <div style={{ fontSize: 26, fontWeight: 800, color, lineHeight: 1 }}>
                              +{p.growth_pct?.toFixed(0) ?? "∞"}%
                            </div>
                            <div style={{ fontSize: 10, color: `${color}bb`, fontWeight: 600, marginTop: 3 }}>зріст</div>
                          </div>
                          <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 36 }}>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                              <div style={{ width: 14, height: prevH, background: `${color}40`, borderRadius: "3px 3px 0 0" }} />
                              <span style={{ fontSize: 8, color: C.text4 }}>{p.prev_week_qty}</span>
                            </div>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                              <div style={{ width: 14, height: currH, background: color, borderRadius: "3px 3px 0 0" }} />
                              <span style={{ fontSize: 8, color }}>{p.curr_week_qty}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            }
          </div>
        );
      })()}

      {/* Під наглядом — товари що вийшли з мертвого запасу (7 днів моніторингу) */}
      {revivedStock.length > 0 && (
        <div style={{ ...S.card, marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 20 }}>👁</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700 }}>Під наглядом</div>
                <div style={{ fontSize: 11, color: C.text4, marginTop: 1 }}>
                  вийшли з мертвого запасу · моніторинг 7 днів
                </div>
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 11, color: C.text3 }}>{revivedStock.length} товарів</span>
              {revivedStock.length > 15 && (
                <span style={{ fontSize: 12, color: C.blue, cursor: "pointer", textDecoration: "underline dotted" }} onClick={() => goTo("/trending")}>Всі →</span>
              )}
            </div>
          </div>
          <div style={{ display: "grid", gap: 10, gridTemplateColumns: `repeat(${Math.min(5, Math.max(2, Math.floor(window.innerWidth / 220)))}, 1fr)` }}>
            {revivedStock.slice(0, 15).map((p, i) => {
              const isStalling = p.daily_sales.slice(-2).every(v => v === 0);
              const stock = p.is_ff ? p.ff_stock : p.fbs_stock;
              const stockOk = stock >= 3;
              const color = isStalling ? C.pink : C.blue;
              const maxBar = Math.max(...p.daily_sales, 1);
              return (
                <div key={i} style={{
                  minWidth: 0, overflow: "hidden",
                  background: `${color}0e`, border: `1px solid ${color}30`,
                  borderTop: `3px solid ${color}`, borderRadius: 12,
                  padding: "14px 14px 12px", display: "flex", flexDirection: "column", gap: 6,
                  transition: "all .15s",
                }}
                  onMouseEnter={e => { e.currentTarget.style.background = `${color}18`; e.currentTarget.style.transform = "translateY(-1px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = `${color}0e`; e.currentTarget.style.transform = ""; }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <Img src={p.image_url} size={24} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <ProdLink name={p.name} article={p.article} productId={p.product_id} marketplace={p.marketplace} style={{ fontSize: 10, color: C.text3, fontWeight: 400 }} />
                    </div>
                  </div>
                  <div style={{ fontSize: 10, color, fontWeight: 700 }}>
                    {isStalling ? "⚠ Знову зупинився" : (p.days_since_revival === 0 ? "↑ ожило сьогодні" : `↑ ожило ${p.days_since_revival} дн. тому`)}
                  </div>
                  <div style={{ fontSize: 10, color: C.text4 }}>Мертвий: {p.gap_days} дн. · {p.recent_qty} шт. з відновлення</div>
                  {/* 7-day sparkline */}
                  <div style={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
                    {p.daily_sales.map((v, di) => (
                      <div key={di} style={{ width: 10, flexShrink: 0, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                        <div style={{
                          width: "100%", background: v > 0 ? color : `${color}20`,
                          height: v > 0 ? Math.max(4, Math.round(v / maxBar * 24)) : 3,
                          borderRadius: "2px 2px 0 0",
                        }} />
                        <div style={{ fontSize: 8, color: v > 0 ? C.text3 : C.text4, lineHeight: 1 }}>{v || "·"}</div>
                      </div>
                    ))}
                  </div>
                  {/* Stock indicator */}
                  <div style={{ fontSize: 10, color: stockOk ? C.green : C.pink, fontWeight: 600 }}>
                    {p.is_ff ? "FF" : "FBS"}: {stock} шт.{!stockOk ? " ⚠" : ""}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Топ товари — подіум + список */}
      <div style={{ ...S.card, overflow: "hidden" }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 16, display: "flex", alignItems: "center", gap: 6 }}>
          🏆 Топ товари за виручкою
          <InfoTip text={"Найприбутковіші товари за обраний аналітичний період.\nКлікніть на назву — відкриє товар на Rozetka."} />
        </div>
        {topProducts.length === 0
          ? <div style={{ color: C.text3, fontSize: 13 }}>Немає даних за цей період</div>
          : (() => {
            const MEDALS = [
              { emoji: "🥇", color: "#f59e0b" },
              { emoji: "🥈", color: "#94a3b8" },
              { emoji: "🥉", color: "#b45309" },
            ];
            const podium = topProducts.slice(0, 3);
            const rest = topProducts.slice(3);
            return (
              <>
                {/* Podium top-3 */}
                <div className="podium-grid" style={{ marginBottom: 16 }}>
                  {podium.map((p, i) => {
                    const m = MEDALS[i];
                    return (
                      <div key={p.product_id} style={{
                        minWidth: 0,
                        background: `${m.color}0c`, border: `1px solid ${m.color}40`,
                        borderTop: `3px solid ${m.color}`, borderRadius: 14,
                        padding: i === 0 ? "18px 12px 14px" : "14px 10px 12px",
                        display: "flex", flexDirection: "column", alignItems: "center", gap: 6, textAlign: "center",
                      }}>
                        <div style={{ fontSize: i === 0 ? 26 : 20 }}>{m.emoji}</div>
                        <Img src={p.image_url} size={i === 0 ? 44 : 36} />
                        <div style={{ width: "100%", overflow: "hidden" }}>
                          <ProdLink name={p.name} article={p.article} productId={p.product_id} marketplace={p.marketplace}
                            style={{ fontSize: 10, color: C.text2, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} />
                        </div>
                        <div style={{ fontSize: i === 0 ? 17 : 14, fontWeight: 800, color: m.color, lineHeight: 1 }}>
                          {fmtUAH(p.revenue)}
                        </div>
                        <div style={{ fontSize: 10, color: C.text4 }}>{fmt(p.quantity)} шт.</div>
                        <div style={{ width: "100%", height: 4, background: C.bg3, borderRadius: 2, overflow: "hidden" }}>
                          <div style={{ height: "100%", borderRadius: 2, background: m.color, width: Math.max(6, p.revenue / maxRevenue * 100) + "%" }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                {/* List #4-10 */}
                {rest.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {rest.map((p, i) => (
                      <div key={p.product_id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 8px", background: C.bg1, borderRadius: 8 }}>
                        <span style={{ fontSize: 11, color: C.text4, fontWeight: 700, width: 18, flexShrink: 0, textAlign: "center" }}>{i + 4}</span>
                        <Img src={p.image_url} size={24} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <ProdLink name={p.name} article={p.article} productId={p.product_id} marketplace={p.marketplace}
                            style={{ fontSize: 11, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} />
                          <div style={{ height: 5, background: C.bg3, borderRadius: 2, overflow: "hidden", marginTop: 3 }}>
                            <div style={{ height: "100%", borderRadius: 2, background: `${C.blue}80`, width: Math.max(4, p.revenue / maxRevenue * 100) + "%" }} />
                          </div>
                        </div>
                        <div style={{ textAlign: "right", flexShrink: 0 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: C.text1 }}>{fmtUAH(p.revenue)}</div>
                          <div style={{ fontSize: 10, color: C.text4 }}>{fmt(p.quantity)} шт.</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            );
          })()
        }
      </div>
    </>
  );
}
